import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  FileText,
  Target,
  Layers,
  Settings,
  Gauge,
  AlertTriangle,
  Cog,
  Activity,
  Ruler,
  ExternalLink,
  CircleDot,
  ShieldCheck,
  HelpCircle,
  BarChart3,
  TrendingUp,
  Search,
  ClipboardCheck,
  BadgeCheck,
  XCircle,
  DollarSign,
  Timer,
  ThumbsUp,
  ThumbsDown,
  Scale,
  FileCheck,
  Cpu,
  Hand,
  Zap,
  RefreshCw,
  Users,
  Award,
  Package
} from 'lucide-react';

const PressiometreChoixGuide = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Composition d'un pressiomètre" },
    { id: 'section-2', title: "Manuel ou automatique ?" },
    { id: 'section-3', title: "Neuf, occasion ou location ?" },
    { id: 'section-4', title: "Acheter d'occasion : vigilance" },
    { id: 'section-5', title: "Au-delà du matériel" },
    { id: 'section-6', title: "Questions fréquentes" },
  ];

  const faqs = [
    {
      question: "Faut-il obligatoirement un pressiomètre automatique ?",
      answer: "Ce n'est pas une obligation normative générale. En revanche, certains cahiers des charges et marchés l'exigent explicitement pour garantir reproductibilité et traçabilité. Vérifiez les CCTP des marchés que vous visez avant de choisir."
    },
    {
      question: "À quelle fréquence faut-il étalonner son pressiomètre ?",
      answer: "Le contrôle de l'appareil est annuel, et sa date de validité doit être vérifiée avant utilisation. Un appareil dont l'étalonnage a expiré produit des mesures non recevables."
    },
    {
      question: "Peut-on louer un pressiomètre ponctuellement ?",
      answer: "Oui, et c'est souvent la meilleure solution pour un besoin ponctuel, un pic d'activité, ou pour répondre à un marché exigeant un automatique sans investir. Le matériel loué chez un spécialiste arrive étalonné et vérifié."
    },
    {
      question: "Combien de temps dure une sonde pressiométrique ?",
      answer: "Cela dépend entièrement de l'intensité d'usage et des terrains rencontrés. Les membranes sont des consommables : leur remplacement fait partie du coût d'exploitation normal, à budgéter dès l'achat."
    },
    {
      question: "Manuel ou automatique pour débuter ?",
      answer: "Si vous démarrez une activité pressiométrique, la location permet de valider votre marché avant d'immobiliser du capital — et de savoir si vos donneurs d'ordre exigent de l'automatique."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Choisir son pressiomètre : manuel ou automatique, neuf ou occasion ? | Guide",
    "description": "Pressiomètre manuel ou auto-contrôlé, neuf, occasion ou location : critères de choix, exigences des marchés, étalonnage et points de vigilance à l'achat.",
    "image": "https://geots.consulting/og-images/guide-pressiometre-choix.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-07-20",
    "dateModified": "2026-07-20"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Pressiometer components
  const pressiometerComponents = [
    { name: "Contrôleur pression-volume (CPV)", desc: "Le cœur du dispositif. Régule la pression et mesure le volume injecté.", icon: Gauge },
    { name: "Sonde pressiométrique", desc: "Tri-cellulaire (cellule centrale + 2 cellules de garde), diamètre 63 ou 76 mm.", icon: CircleDot },
    { name: "Tubulures", desc: "Coaxiales ou jumelées. Règle : utiliser la plus courte possible par rapport à la profondeur.", icon: Activity },
    { name: "Accessoires", desc: "Trépied, gaine de protection, train de tiges.", icon: Package },
  ];

  // Comparison data
  const comparisonData = [
    { criteria: "Investissement", manual: "Modéré", auto: "Élevé" },
    { criteria: "Reproductibilité", manual: "Dépend de l'opérateur", auto: "Excellente" },
    { criteria: "Traçabilité des données", manual: "Saisie manuelle", auto: "Enregistrement automatique" },
    { criteria: "Cadence / dépouillement", manual: "Plus lent", auto: "Rapide" },
    { criteria: "Très hautes pressions", manual: "Limité", auto: "Modèles dédiés disponibles" },
    { criteria: "Éligibilité marchés exigeants", manual: "⚠️ Parfois insuffisant", auto: "Conforme aux exigences" },
    { criteria: "Maintenance", manual: "Simple", auto: "Spécialisée" },
  ];

  // Acquisition modes
  const acquisitionModes = [
    { 
      type: "Neuf", 
      pertinent: "Activité pressiométrique régulière et structurante",
      avantages: "Garantie constructeur, conforme aux dernières normes, formation incluse, SAV",
      vigilance: "Investissement important, délai de livraison",
      color: "bg-green-50 border-green-200",
      textColor: "text-green-800"
    },
    { 
      type: "Occasion", 
      pertinent: "Compétence technique interne et budget contraint",
      avantages: "Coût d'entrée réduit, disponibilité immédiate",
      vigilance: "Points de vérification impératifs (voir ci-dessous)",
      color: "bg-blue-50 border-blue-200",
      textColor: "text-blue-800"
    },
    { 
      type: "Location", 
      pertinent: "Besoin ponctuel, pic d'activité, test de marché",
      avantages: "Aucune immobilisation, matériel étalonné et vérifié, pas de gestion métrologique",
      vigilance: "Coût cumulé si usage très fréquent",
      color: "bg-purple-50 border-purple-200",
      textColor: "text-purple-800"
    },
  ];

  // Vigilance points for used equipment
  const vigilancePoints = [
    { point: "Certificat d'étalonnage du CPV", desc: "Le point n°1. Contrôle annuel obligatoire. Sans étalonnage valide, l'appareil n'est pas exploitable.", critical: true, icon: BadgeCheck },
    { point: "État des sondes", desc: "Membranes (fatigue, déformation, éclatement), cellules de garde, raccords. Consommables à durée de vie limitée.", critical: true, icon: CircleDot },
    { point: "Tubulures", desc: "Étanchéité, état des raccords haute pression, longueur disponible vs profondeurs visées.", critical: false, icon: Activity },
    { point: "Étanchéité générale du circuit", desc: "Absence de fuite en pression.", critical: false, icon: ShieldCheck },
    { point: "État de l'électronique (automatique)", desc: "Capteurs, mises à jour logicielles, support technique encore assuré par le fabricant.", critical: true, icon: Cpu },
    { point: "Conformité normative", desc: "Permet-il des essais conformes à la NF EN ISO 22476-4 (2021) ?", critical: true, icon: FileCheck },
    { point: "Complétude du lot", desc: "CPV + sondes + tubulures + trépied + accessoires. Un lot incomplet coûte cher à compléter.", critical: false, icon: Package },
  ];

  return (
    <>
      <Helmet>
        <title>Choisir son pressiomètre : manuel ou automatique, neuf ou occasion ? | Guide</title>
        <meta name="description" content="Pressiomètre manuel ou auto-contrôlé, neuf, occasion ou location : critères de choix, exigences des marchés, étalonnage et points de vigilance à l'achat." />
        <link rel="canonical" href="https://geots.consulting/guides/choisir-pressiometre-manuel-automatique" />
        <meta property="og:title" content="Choisir son pressiomètre : manuel ou automatique, neuf ou occasion ? | Guide" />
        <meta property="og:description" content="Pressiomètre manuel ou auto-contrôlé, neuf, occasion ou location : critères de choix, exigences des marchés, étalonnage et points de vigilance à l'achat." />
        <meta property="og:url" content="https://geots.consulting/guides/choisir-pressiometre-manuel-automatique" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
          <div className="container mx-auto px-4 py-12 md:py-20">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8 text-gray-300">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-white">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Choisir son pressiomètre</span>
            </nav>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-[#635BFF]/20 text-[#00D4FF] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Gauge className="w-4 h-4" />
                Guide d'achat
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                Comment choisir son pressiomètre :
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00D4FF] to-[#635BFF] mt-2">
                  manuel ou automatique, neuf ou occasion ?
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
                S'équiper en pressiométrie, c'est arbitrer sur deux axes : la technologie (contrôleur manuel ou pressiomètre auto-contrôlé) 
                et le mode d'acquisition (neuf, occasion ou location). Un investissement structurant pour un bureau d'études comme pour 
                une entreprise de sondage. Voici les critères qui comptent vraiment.
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Mis à jour : Juillet 2026</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Temps de lecture : 10 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white rounded-xl shadow-lg border p-6">
                  <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#635BFF]" />
                    Sommaire
                  </h3>
                  <nav className="space-y-1">
                    {sections.map((section, index) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                          activeSection === section.id
                            ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="line-clamp-2">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Related Guides */}
                <div className="bg-gradient-to-br from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 mt-6 border border-[#635BFF]/10">
                  <h4 className="font-bold text-[#0A2540] mb-4">Guides connexes</h4>
                  <div className="space-y-3">
                    <Link to="/guides/essai-pressiometrique-menard" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Essai pressiométrique Ménard
                    </Link>
                    <Link to="/guides/materiel-de-forage" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Matériel de forage
                    </Link>
                    <Link to="/guides/foreuse-occasion-micropieux" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Foreuse d'occasion micropieux
                    </Link>
                    <Link to="/guides/enregistreur-parametres-forage" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Enregistreur de paramètres
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Article */}
            <article className="flex-1 max-w-3xl">
              {/* Section 1: Composition */}
              <section id="section-1" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#635BFF]/10 flex items-center justify-center">
                    <Package className="w-5 h-5 text-[#635BFF]" />
                  </span>
                  Rappel : de quoi se compose un pressiomètre ?
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700 mb-6">
                  <p>
                    Avant de choisir, il faut comprendre que le « pressiomètre » n'est pas un objet unique mais une 
                    <strong> chaîne d'équipements</strong> dont chaque maillon conditionne la qualité de la mesure :
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  {pressiometerComponents.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-white rounded-xl p-5 border shadow-sm flex items-start gap-4"
                      >
                        <div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-6 h-6 text-[#635BFF]" />
                        </div>
                        <div>
                          <h3 className="font-bold text-[#0A2540] mb-1">{item.name}</h3>
                          <p className="text-gray-600 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 border-l-4 border-[#635BFF]">
                  <p className="text-gray-700 mb-3">
                    L'essai est encadré par la norme <strong>NF EN ISO 22476-4</strong> (2ᵉ édition, septembre 2021).
                  </p>
                  <Link 
                    to="/guides/essai-pressiometrique-menard"
                    className="inline-flex items-center gap-2 text-[#635BFF] font-medium hover:underline"
                  >
                    Voir notre guide sur l'essai pressiométrique Ménard
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </section>

              {/* Section 2: Manual vs Automatic */}
              <section id="section-2" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Scale className="w-5 h-5 text-blue-600" />
                  </span>
                  Premier arbitrage : manuel ou automatique ?
                </h2>

                {/* Manual */}
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Hand className="w-6 h-6 text-amber-700" />
                    </div>
                    <h3 className="font-bold text-amber-800 text-xl">Le pressiomètre manuel (CPV classique)</h3>
                  </div>
                  <p className="text-amber-700 mb-4">
                    L'opérateur pilote lui-même la montée en pression, palier par palier, et relève les valeurs. 
                    Les modèles récents disposent d'afficheurs numériques avec une lecture plus fine.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-bold text-green-700 mb-2 flex items-center gap-2">
                        <ThumbsUp className="w-4 h-4" /> Ses atouts
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Investissement plus accessible</li>
                        <li>• Robustesse et simplicité mécanique</li>
                        <li>• Réparable et maintenable facilement</li>
                        <li>• Adapté aux volumes d'essais modérés</li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-bold text-red-700 mb-2 flex items-center gap-2">
                        <ThumbsDown className="w-4 h-4" /> Ses limites
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Qualité dépend du savoir-faire opérateur</li>
                        <li>• Risque de variabilité entre opérateurs</li>
                        <li>• Saisie manuelle chronophage</li>
                        <li>• Exposé aux erreurs de report</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Automatic */}
                <div className="bg-cyan-50 border-2 border-cyan-200 rounded-xl p-6 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
                      <Cpu className="w-6 h-6 text-cyan-700" />
                    </div>
                    <h3 className="font-bold text-cyan-800 text-xl">Le pressiomètre automatique (auto-contrôlé)</h3>
                  </div>
                  <p className="text-cyan-700 mb-4">
                    Une fois la sonde en place, l'opérateur lance l'essai depuis une unité de pilotage : 
                    <strong> l'essai se déroule ensuite de façon autonome</strong> — variation des paliers, gestion 
                    de la pression différentielle, dégonflage — pendant que l'opérateur supervise à l'écran.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-bold text-green-700 mb-2 flex items-center gap-2">
                        <ThumbsUp className="w-4 h-4" /> Ses atouts
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Reproductibilité</strong> : protocole identique</li>
                        <li>• <strong>Traçabilité</strong> : enregistrement automatique</li>
                        <li>• Précision accrue pression/volume</li>
                        <li>• Gain de temps dépouillement</li>
                        <li>• Modèles très haute pression (25-30 MPa)</li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-bold text-red-700 mb-2 flex items-center gap-2">
                        <ThumbsDown className="w-4 h-4" /> Ses limites
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Investissement plus élevé</li>
                        <li>• Dépendance électronique/alimentation</li>
                        <li>• Maintenance plus spécialisée</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Decision point */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-red-800 mb-2">⚠️ Le point qui fait souvent basculer la décision</h4>
                      <p className="text-red-700 mb-3">
                        <strong>Certains marchés et cahiers des charges exigent aujourd'hui explicitement un pressiomètre automatique</strong> 
                        — pour garantir la reproductibilité des essais et disposer de données brutes enregistrées et non retranscrites à la main.
                      </p>
                      <p className="text-red-700 font-medium">
                        👉 Vérifiez donc en amont les CCTP des marchés que vous visez. S'équiper en manuel alors que vos donneurs d'ordre 
                        exigent de l'automatique, c'est se fermer des appels d'offres.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Comparison table */}
                <h3 className="font-bold text-[#0A2540] text-lg mb-4">Tableau de synthèse</h3>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                    <thead>
                      <tr className="bg-[#0A2540] text-white">
                        <th className="text-left p-4 font-semibold">Critère</th>
                        <th className="text-left p-4 font-semibold">Manuel</th>
                        <th className="text-left p-4 font-semibold">Automatique</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.map((row, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="p-4 font-medium text-[#0A2540]">{row.criteria}</td>
                          <td className="p-4 text-gray-600">{row.manual}</td>
                          <td className="p-4 text-gray-600">{row.auto}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 border">
                  <p className="text-gray-700">
                    <strong>En résumé :</strong> le manuel reste pertinent pour un volume d'essais modéré et une équipe expérimentée. 
                    L'automatique s'impose dès que vous visez des marchés exigeants, un fort volume d'essais, ou des terrains très durs.
                  </p>
                </div>
              </section>

              {/* Section 3: New, Used or Rental */}
              <section id="section-3" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </span>
                  Second arbitrage : neuf, occasion ou location ?
                </h2>

                <div className="space-y-4 mb-6">
                  {acquisitionModes.map((mode, index) => (
                    <div 
                      key={index}
                      className={`rounded-xl p-6 border-2 ${mode.color}`}
                    >
                      <h3 className={`font-bold text-xl mb-3 ${mode.textColor}`}>{mode.type}</h3>
                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="font-medium text-gray-700 mb-1">Pertinent si :</p>
                          <p className="text-gray-600">{mode.pertinent}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4 text-green-500" /> Avantages :
                          </p>
                          <p className="text-gray-600">{mode.avantages}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4 text-amber-500" /> Vigilance :
                          </p>
                          <p className="text-gray-600">{mode.vigilance}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 border-l-4 border-[#00D4FF]">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-6 h-6 text-[#00D4FF] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#0A2540] mb-2">💡 Un cas de figure fréquent</h4>
                      <p className="text-gray-700">
                        Une entreprise équipée en manuel qui décroche un marché exigeant l'automatique. 
                        Plutôt que d'investir dans l'urgence, la <strong>location d'un pressiomètre auto-contrôlé pour la durée du marché</strong> 
                        est souvent la solution la plus rationnelle.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 4: Used equipment vigilance */}
              <section id="section-4" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <Search className="w-5 h-5 text-red-600" />
                  </span>
                  Acheter un pressiomètre d'occasion : les points de vigilance
                </h2>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <p className="text-amber-800">
                    Un pressiomètre est un <strong>instrument de mesure</strong>, pas un simple outil. Un appareil dérivé produit 
                    des courbes fausses — et donc un dimensionnement de fondations erroné. Les vérifications sont plus exigeantes 
                    que pour un engin classique.
                  </p>
                </div>

                <div className="space-y-3">
                  {vigilancePoints.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <div 
                        key={index}
                        className={`rounded-xl p-4 border-2 ${
                          item.critical 
                            ? 'bg-red-50 border-red-200' 
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            item.critical ? 'bg-red-100' : 'bg-gray-100'
                          }`}>
                            <IconComponent className={`w-5 h-5 ${item.critical ? 'text-red-600' : 'text-gray-600'}`} />
                          </div>
                          <div>
                            <h4 className={`font-bold mb-1 ${item.critical ? 'text-red-800' : 'text-[#0A2540]'}`}>
                              {item.point}
                              {item.critical && <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">CRITIQUE</span>}
                            </h4>
                            <p className={`text-sm ${item.critical ? 'text-red-700' : 'text-gray-600'}`}>{item.desc}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Section 5: Beyond equipment */}
              <section id="section-5" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Award className="w-5 h-5 text-purple-600" />
                  </span>
                  Au-delà du matériel : la qualité de l'essai
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700 mb-6">
                  <p>
                    Un point souvent négligé : <strong>le meilleur pressiomètre du monde ne rattrapera jamais un mauvais forage.</strong> 
                    La qualité du préforage — parois non remaniées, diamètre adapté à la sonde, terrain environnant le moins perturbé 
                    possible — conditionne la validité de l'essai autant que l'appareil.
                  </p>
                  <p>
                    C'est d'ailleurs l'un des enseignements du <strong>projet national ARSCOP</strong>, qui a produit des fiches pratiques 
                    à destination des équipes de terrain pour améliorer la qualité des essais pressiométriques.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] rounded-xl p-6 text-white">
                  <h3 className="font-bold text-xl mb-4">Le trio gagnant</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-white/10 rounded-lg p-4 text-center">
                      <RefreshCw className="w-8 h-8 mx-auto mb-2 text-[#00D4FF]" />
                      <p className="font-medium">Un matériel étalonné</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4 text-center">
                      <Target className="w-8 h-8 mx-auto mb-2 text-[#00D4FF]" />
                      <p className="font-medium">Un forage soigné</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4 text-center">
                      <Users className="w-8 h-8 mx-auto mb-2 text-[#00D4FF]" />
                      <p className="font-medium">Un opérateur formé</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 6: FAQ */}
              <section id="section-6" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-indigo-600" />
                  </span>
                  Questions fréquentes
                </h2>

                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-xl border overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-[#0A2540] pr-4">{faq.question}</span>
                        {openFaq === index ? (
                          <ChevronUp className="w-5 h-5 text-[#635BFF] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {openFaq === index && (
                        <div className="px-5 pb-5 text-gray-600 border-t">
                          <p className="pt-4">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* CTA Box */}
              <div className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] rounded-2xl p-8 text-white mb-8">
                <h3 className="text-xl font-bold mb-4">
                  S'équiper en pressiométrie : matériel, étalonnage et accompagnement
                </h3>
                <p className="text-gray-300 mb-6">
                  Choisir un pressiomètre, c'est arbitrer entre technologie, budget et exigences de vos marchés — sans jamais 
                  transiger sur l'<strong>étalonnage</strong> et la <strong>conformité normative</strong>, qui conditionnent 
                  la validité de vos essais.
                </p>
                <p className="text-gray-300 mb-6">
                  GEOTS met en relation les acteurs de la géotechnique : fournisseurs et loueurs de matériel pressiométrique 
                  et d'instrumentation, spécialistes de l'étalonnage et de la maintenance.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5248d9] text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Contactez-nous
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link 
                    to="/annuaire/ingenierie-etudes-geotechniques/essais-pressiometriques-penetrometriques"
                    className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Voir l'annuaire pressiométrie
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="text-sm text-gray-500 italic border-t pt-8">
                Ce guide est fourni à titre informatif. La réalisation des essais pressiométriques doit être conforme à la norme 
                NF EN ISO 22476-4 et confiée à des opérateurs formés, avec un matériel régulièrement étalonné.
              </div>
            </article>
          </div>
        </div>

        {/* Related Guides Section */}
        <div className="bg-gray-50 border-t py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-8 text-center">
              Continuez votre lecture
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Link 
                to="/guides/essai-pressiometrique-menard"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  L'essai pressiométrique Ménard
                </h3>
                <p className="text-gray-600 text-sm">
                  EM, pl*, interprétation et dimensionnement.
                </p>
              </Link>

              <Link 
                to="/guides/materiel-de-forage"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                  <Cog className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Matériel de forage : guide complet
                </h3>
                <p className="text-gray-600 text-sm">
                  Foreuses, outils et instrumentation.
                </p>
              </Link>

              <Link 
                to="/guides/foreuse-occasion-micropieux"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                  <Search className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Foreuse d'occasion pour micropieux
                </h3>
                <p className="text-gray-600 text-sm">
                  Guide d'achat, critères et pièges à éviter.
                </p>
              </Link>

              <Link 
                to="/guides/etalonnage-capteurs-forage"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                  <Settings className="w-6 h-6 text-slate-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Étalonnage des capteurs de forage
                </h3>
                <p className="text-gray-600 text-sm">
                  Obligations, ISO 17025 et bonnes pratiques.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PressiometreChoixGuide;
